import {ResourcesConfig} from 'aws-amplify';
import {stage} from '../stage';

// Cognito wiring — shared platform values hardcoded per stage; per-app
// pieces (callback URL + optional identity-pool override) come from env
// vars populated at provisioning time by the FNP-60 orchestrator.
//
// Why hardcoded?
//   The user pool, user pool client, OAuth (broker) domain, and signOut redirect
//   are shared across every authenticated SPA on the platform — survey
//   of bot, email, console, and portal showed identical values per
//   stage. Asking the orchestrator to populate them per app is
//   pointless duplication.
//
// Why env-driven for redirectSignIn?
//   It's the only piece that's truly per-app — Cognito redirects back
//   to whichever subdomain initiated sign-in.
//
//   ⚠️ SET VITE_APP_URL BY HAND AFTER CLONING. The seeder does NOT write
//   it. `buildSourceTemplateVariables` substitutes exactly three tokens —
//   APP_NAME, DISPLAY_NAME and DESCRIPTION — so an unedited `.env.<mode>`
//   leaves VITE_APP_URL blank and sign-in redirects to the wrong origin.
//
// Why optional identity-pool override?
//   Customer-facing apps share one identity pool with default IAM roles.
//   Admin apps (like console) get their own with admin-grade roles. If
//   that's you, set VITE_COGNITO_IDENTITY_POOL_ID at provisioning time.
//
// FNP-872: sign-in is code + PKCE through the auth broker. `domain` is the
// broker's execute-api host plus the `/prod` stage path; Amplify builds
// `https://${domain}/oauth2/...` by concatenation. The broker hands the
// portal-issued tokens back, including a refresh token.
//
// Brittleness: if these pool IDs ever rotate, every seeded app breaks.
// The eventual follow-up is to read everything from a platform-cognito-
// config DDB record at orchestrator-run time. Tracked separately.

type StageConfig = {
    userPoolId: string;
    userPoolClientId: string;
    identityPoolId: string;
    region: string;
    domain: string;
    redirectSignOutUrl: string;
};

export const platformCognito: Record<'prod' | 'test' | 'dev' | 'ide', StageConfig> = {
    prod: {
        userPoolId: 'us-west-2_bxdNeqDGn',
        userPoolClientId: '467rlh361q573kme7313svd3sb',
        identityPoolId: 'us-west-2:a566963b-048f-4ffa-9df6-89814738257a',
        region: 'us-west-2',
        domain: 'aty3ojbu1h.execute-api.us-west-2.amazonaws.com/prod',
        redirectSignOutUrl: 'https://portal.flexi.network',
    },
    test: {
        userPoolId: 'us-west-2_UuiFFepVO',
        userPoolClientId: '4l6lu4764mgkk0s6jfq5ebq2c8',
        identityPoolId: 'us-west-2:d7a2833c-863d-46e4-85a3-16b116ef25fc',
        region: 'us-west-2',
        domain: 'zk71rojmk1.execute-api.us-west-2.amazonaws.com/prod',
        redirectSignOutUrl: 'https://portal.test.flexi.network',
    },
    dev: {
        userPoolId: 'us-west-2_INBgfMat1',
        userPoolClientId: '52m9svts4nvks7qi0ft8lt0oct',
        identityPoolId: 'us-west-2:d806f8ff-118f-4444-917f-e85833880089',
        region: 'us-west-2',
        domain: 'ft8v2ll4uc.execute-api.us-west-2.amazonaws.com/prod',
        redirectSignOutUrl: 'https://portal.dev.flexi.network',
    },
    // ⚠️ The 8011 below is the PORTAL, not this app. This app serves on
    // 8000 (`server.port` in vite.config.ts). `redirectSignOutUrl` returns
    // the user to the portal after sign-out, so it stays 8011 locally.
    // `domain` is the DEV broker: ide has no broker of its own. Only
    // redirectSignIn is this app's own origin, and it comes from
    // VITE_APP_URL / window.location.origin (8000) below.
    ide: {
        userPoolId: 'us-west-2_INBgfMat1',
        userPoolClientId: '52m9svts4nvks7qi0ft8lt0oct',
        identityPoolId: 'us-west-2:d806f8ff-118f-4444-917f-e85833880089',
        region: 'us-west-2',
        domain: 'ft8v2ll4uc.execute-api.us-west-2.amazonaws.com/prod',
        redirectSignOutUrl: 'https://localhost:8011',
    },
};

const platform = platformCognito[stage] ?? platformCognito.dev;

// Per-app callback URL. Set VITE_APP_URL by hand in each `.env.<mode>`
// after cloning — the seeder does not populate it.
//
// ⚠️ `||`, NOT `??`. Vite substitutes a declared-but-blank env var with an
// EMPTY STRING, and `??` only catches null/undefined — so `??` let `''`
// through and produced `redirectSignIn: ['']`, which breaks the OAuth
// redirect with no error at build time. `||` catches the empty string.
const redirectSignIn =
    import.meta.env.VITE_APP_URL ||
    (typeof window !== 'undefined' ? window.location.origin : '');

// Optional identity-pool override for admin apps that need their own
// IAM role set. Customer-facing apps leave this blank and inherit the
// shared platform pool.
const identityPoolId =
    import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID || platform.identityPoolId;

export const config: ResourcesConfig['Auth'] = {
    Cognito: {
        userPoolId: platform.userPoolId,
        userPoolClientId: platform.userPoolClientId,
        identityPoolId,
        allowGuestAccess: true,
        loginWith: {
            oauth: {
                domain: platform.domain,
                scopes: [
                    'phone',
                    'email',
                    'profile',
                    'openid',
                    'aws.cognito.signin.user.admin',
                ],
                redirectSignIn: [redirectSignIn],
                redirectSignOut: [platform.redirectSignOutUrl],
                responseType: 'code',
            },
        },
    },
};
